import { useState, useEffect, useRef } from 'react'
import { t } from '@/components/common/Helpers'
import PhotoCarouselModal from './PhotoCarouselModal'

export default function HorizontalGallery({
    obituary,
    titleClasses = 'app-title text-4xl mb-6 text-center text-gray-700',
    buttonClasses = 'shadow-sm border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 px-6 py-3 text-sm rounded-md inline-flex items-center justify-center font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-200',
    fitContainer = false,
    isPreviewMode = false
}) {
    const [showFullGallery, setShowFullGallery] = useState(false)
    const [selectedImageIndex, setSelectedImageIndex] = useState(0)
    const [scrollY, setScrollY] = useState(0)
    const [manualScrollOffset, setManualScrollOffset] = useState(0)
    const [isDragging, setIsDragging] = useState(false)
    const [dragStart, setDragStart] = useState({ x: 0, scrollLeft: 0 })
    const [hasDragged, setHasDragged] = useState(false)
    const galleryRef = useRef(null)
    const scrollContainerRef = useRef(null)

    useEffect(() => {
        const handleScroll = () => {
            setScrollY(window.scrollY)
        }

        window.addEventListener('scroll', handleScroll, { passive: true })
        return () => {
            window.removeEventListener('scroll', handleScroll)
        }
    }, [])

    // Mouse and touch event handlers for manual scrolling
    const handleMouseDown = (e) => {
        if (isPreviewMode) return
        setIsDragging(true)
        setHasDragged(false)
        setDragStart({
            x: e.clientX,
            scrollLeft: manualScrollOffset
        })
    }

    const handleMouseMove = (e) => {
        if (!isDragging || isPreviewMode) return
        e.preventDefault()
        const deltaX = e.clientX - dragStart.x
        const newOffset = dragStart.scrollLeft + deltaX
        
        // Calculate scroll bounds and constrain offset
        const { minScroll, maxScroll } = getScrollBounds()
        const constrainedOffset = Math.max(minScroll, Math.min(maxScroll, newOffset))
        setManualScrollOffset(constrainedOffset)
        
        // Mark as dragged if movement is significant
        if (Math.abs(deltaX) > 5) {
            setHasDragged(true)
        }
    }

    const handleMouseUp = () => {
        setIsDragging(false)
    }

    const handleTouchStart = (e) => {
        if (isPreviewMode) return
        setIsDragging(true)
        setHasDragged(false)
        setDragStart({
            x: e.touches[0].clientX,
            scrollLeft: manualScrollOffset
        })
    }

    const handleTouchMove = (e) => {
        if (!isDragging || isPreviewMode) return
        e.preventDefault()
        const deltaX = e.touches[0].clientX - dragStart.x
        const newOffset = dragStart.scrollLeft + deltaX
        
        // Calculate scroll bounds and constrain offset
        const { minScroll, maxScroll } = getScrollBounds()
        const constrainedOffset = Math.max(minScroll, Math.min(maxScroll, newOffset))
        setManualScrollOffset(constrainedOffset)
        
        // Mark as dragged if movement is significant
        if (Math.abs(deltaX) > 5) {
            setHasDragged(true)
        }
    }

    const handleTouchEnd = () => {
        setIsDragging(false)
    }

    // Add event listeners for mouse and touch events
    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove)
            document.addEventListener('mouseup', handleMouseUp)
            document.addEventListener('touchmove', handleTouchMove, { passive: false })
            document.addEventListener('touchend', handleTouchEnd)
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove)
            document.removeEventListener('mouseup', handleMouseUp)
            document.removeEventListener('touchmove', handleTouchMove)
            document.removeEventListener('touchend', handleTouchEnd)
        }
    }, [isDragging, dragStart])

    if (!obituary || !obituary.gallery_photos || obituary.gallery_photos.length === 0) {
        return null
    }

    const photos = obituary.gallery_photos.map((photo, index) => ({
        original: photo.xl,
        thumbnail: photo.md,
        originalAlt: `${obituary.display_name} photo ${index + 1} of ${obituary.gallery_photos.length}`,
        thumbnailAlt: `${obituary.display_name} photo thumbnail ${index + 1} of ${obituary.gallery_photos.length}`,
        thumbnailLoading: "lazy",
        loading: "lazy",
    }))

    // Load first 30 photos
    const displayPhotos = photos.slice(0, 30)
    const originalDisplayCount = displayPhotos.length
    
    // Calculate duplicate photos to fill the screen width for better visual effect
    let duplicatePhotos = []
    if (typeof window !== 'undefined') {
        const itemWidth = fitContainer ? 200 : 280
        const gap = 24 // Should match the gap in the flex container
        const containerWidth = window.innerWidth
        const itemWidthWithGap = itemWidth + gap
        
        // Calculate how many items are needed to fill the screen (including placeholder)
        const itemsNeededToFillScreen = Math.ceil(containerWidth / itemWidthWithGap)
        
        // Only duplicate if we need more items to fill the screen
        // Account for placeholder (+1)
        if (displayPhotos.length + 1 < itemsNeededToFillScreen) {
            const duplicatesNeeded = itemsNeededToFillScreen - displayPhotos.length - 1
            for (let i = 0; i < duplicatesNeeded; i++) {
                const photoToDuplicate = displayPhotos[i % originalDisplayCount]
                duplicatePhotos.push({
                    ...photoToDuplicate
                })
            }
        }
    }

    const handleViewGallery = (imageIndex = 0) => {
        if (isPreviewMode) {
            return
        }
        setSelectedImageIndex(imageIndex)
        setShowFullGallery(true)
    }

    const handlePhotoClick = (photoIndex) => {
        if (isPreviewMode || hasDragged) {
            return
        }
        // If it's a duplicate photo, map back to the original photo index
        const originalPhotoIndex = photoIndex >= originalDisplayCount ? photoIndex % originalDisplayCount : photoIndex
        handleViewGallery(originalPhotoIndex)
    }

    // Calculate scroll bounds
    const getScrollBounds = () => {
        const containerWidth = scrollContainerRef.current?.offsetWidth || 0
        const itemWidth = fitContainer ? 200 : 280
        const gap = 24
        
        // Calculate content width including all displayed photos, placeholder, and duplicates
        const totalItems = displayPhotos.length + 1 + duplicatePhotos.length
        const contentWidth = totalItems * itemWidth + (totalItems - 1) * gap

        // The parallax baseOffset starts images at -window.innerWidth * 0.2 to the left
        const baseParallaxOffset = typeof window !== 'undefined' ? window.innerWidth * 0.2 : 0
        
        // Max positive offset: compensate for the parallax baseOffset plus extra room to fully reveal first image
        const maxScroll = baseParallaxOffset + (itemWidth * 2)
        
        // Max negative offset to reveal placeholder at the end
        const minScroll = Math.min(0, -(contentWidth - containerWidth))
        
        return { minScroll, maxScroll, containerWidth, contentWidth }
    }

    // Calculate parallax transform - combine manual scroll with browser scroll parallax
    const getParallaxTransform = (index) => {
        if (typeof window === 'undefined') {
            return 'translateX(0px)'
        }

        const parallaxStrength = 0.2
        const baseOffset = -window.innerWidth * 0.01
        const scrollOffset = -scrollY * parallaxStrength
        
        // Apply scroll bounds to manual scroll offset
        const { minScroll, maxScroll } = getScrollBounds()
        const constrainedManualOffset = Math.max(minScroll, Math.min(maxScroll, manualScrollOffset))
        
        // Calculate total offset before parallax
        let totalOffset = baseOffset + scrollOffset + constrainedManualOffset
        
        // Constrain the total offset to prevent scrolling too far on both sides
        const itemWidth = fitContainer ? 200 : 280
        const isMobile = typeof window !== 'undefined' && window.innerWidth < 768
        const offsetAdjustment = isMobile ? (itemWidth * 0.8) : (itemWidth / 4)
        
        // Prevent scrolling too far LEFT (past the placeholder)
        const maxNegativeOffset = minScroll - offsetAdjustment
        totalOffset = Math.max(maxNegativeOffset, totalOffset)
        
        // Prevent scrolling too far RIGHT (past the first image)
        const maxPositiveOffset = -baseOffset
        totalOffset = Math.min(maxPositiveOffset, totalOffset)
        
        return `translateX(${totalOffset}px)`
    }

    return (
        <>
            <div
                ref={galleryRef}
                className={`relative bg-gray-50 py-12 md:py-16 ${fitContainer
                    ? ''
                    : 'w-screen left-1/2 right-1/2 -ml-[50vw] -mr-[50vw]'
                    }`}
            >

                {!isPreviewMode &&
                    <div className="text-center mb-8 md:mb-12 px-4 md:px-8">
                        <h2 className={titleClasses}>
                            {t.trans('obituaries.photo-gallery-life-of') + ' ' + obituary.first_name}
                        </h2>
                    </div>
                }

                <div 
                    ref={scrollContainerRef}
                    className="flex items-center gap-3 md:gap-4 lg:gap-6 overflow-hidden w-full min-w-full px-4 md:px-8 cursor-grab select-none"
                    style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
                    onMouseDown={handleMouseDown}
                    onTouchStart={handleTouchStart}
                >
                    {displayPhotos.map((photo, index) => (
                        <div
                            key={index}
                            className="group relative flex-shrink-0 overflow-hidden rounded-lg shadow-md hover:shadow-lg transition-all duration-300 bg-white cursor-pointer"
                            style={{
                                width: fitContainer ? 'clamp(120px, 15vw, 200px)' : 'clamp(160px, 20vw, 280px)',
                                height: fitContainer ? 'clamp(160px, 20vw, 267px)' : 'clamp(213px, 26.7vw, 373px)',
                                transform: getParallaxTransform(index),
                                transition: isDragging ? 'none' : 'transform 0.1s ease-out'
                            }}
                            onClick={isPreviewMode ? () => { } : () => handlePhotoClick(index)}
                        >
                            <img
                                src={photo.thumbnail}
                                alt={`${obituary.display_name} photo ${index + 1}`}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                loading="lazy"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        </div>
                    ))}
                    
                    {/* Placeholder */}
                    <div
                        className="group relative flex-shrink-0 overflow-hidden rounded-lg shadow-md hover:shadow-lg transition-all duration-300 bg-gray-100 border-2 border-dashed border-gray-300 flex flex-col items-center justify-center cursor-pointer hover:bg-gray-200"
                        style={{
                            width: fitContainer ? 'clamp(120px, 15vw, 200px)' : 'clamp(160px, 20vw, 280px)',
                            height: fitContainer ? 'clamp(160px, 20vw, 267px)' : 'clamp(213px, 26.7vw, 373px)',
                            transform: getParallaxTransform(displayPhotos.length),
                            transition: isDragging ? 'none' : 'transform 0.1s ease-out'
                        }}
                        onClick={isPreviewMode || hasDragged ? () => { } : () => handleViewGallery(0)}
                    >
                        <div className="text-gray-500 text-sm font-semibold mb-2">
                            {obituary.gallery_photos.length > 30 ? `+${obituary.gallery_photos.length - 30} more photos` : 'View all photos'}
                        </div>
                    </div>
                    
                    {/* Duplicate photos to fill screen */}
                    {duplicatePhotos.map((photo, index) => (
                        <div
                            key={`duplicate-${index}`}
                            className="group relative flex-shrink-0 overflow-hidden rounded-lg shadow-md hover:shadow-lg transition-all duration-300 bg-white cursor-pointer"
                            style={{
                                width: fitContainer ? 'clamp(120px, 15vw, 200px)' : 'clamp(160px, 20vw, 280px)',
                                height: fitContainer ? 'clamp(160px, 20vw, 267px)' : 'clamp(213px, 26.7vw, 373px)',
                                transform: getParallaxTransform(displayPhotos.length + 1 + index),
                                transition: isDragging ? 'none' : 'transform 0.1s ease-out'
                            }}
                            onClick={isPreviewMode ? () => { } : () => handlePhotoClick(index)}
                        >
                            <img
                                src={photo.thumbnail}
                                alt={`${obituary.display_name} photo ${index + 1}`}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                loading="lazy"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        </div>
                    ))}
                </div>

                <div className="text-center mt-8 md:mt-12 px-4 md:px-8">
                    {!isPreviewMode && (
                        <button
                            onClick={() => handleViewGallery(0)}
                            className={buttonClasses}
                        >
                            View All Photos
                        </button>
                    )}
                </div>
            </div>

            <PhotoCarouselModal
                photos={photos}
                isOpen={showFullGallery}
                onClose={() => setShowFullGallery(false)}
                currentPhotoIndex={selectedImageIndex}
                setCurrentPhotoIndex={setSelectedImageIndex}
                showBackgroundBlur={true}
            />
        </>
    )
}
